import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtBRL } from "@/lib/api";
import { CheckCircle2, HandCoins, Loader2 } from "lucide-react";

export const trocoSaidaPendente = (x) => (x.status === "aguardando_conferencia" || x.status === "em_transito") && (Number(x.troco) || 0) > 0 && !x.troco_saida_confirmado;
export const somaTroco = (list) => list.reduce((a, x) => a + (Number(x.troco) || 0), 0);
const pedidoDe = (x) => x.numero_pedido || x.pedidos_multiplos?.[0] || x.numero_pedido_receita || "s/ nº";

/** Lista compacta "pedido · cliente · troco" (1 linha por entrega). */
function TrocoLista({ entregas }) {
  return (
    <ul className="divide-y divide-amber-200/70 text-sm" data-testid="troco-saida-lista">
      {entregas.map((x) => (
        <li key={x.id} className="flex items-center justify-between py-1 gap-2" data-testid={`troco-saida-item-${x.id}`}>
          <span className="truncate"><b>#{pedidoDe(x)}</b> <span className="text-slate-600">{x.cliente_nome || ""}</span></span>
          <span className="mono font-bold text-amber-900 shrink-0">{fmtBRL(x.troco)}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Iter 119 — card do troco de saída dentro do dialog de aceite ("estou com em mãos").
 * Botão-check: 1 toque marca que recebeu o troco do balcão; o aceite só libera depois.
 */
export function TrocoSaidaCheck({ entregas, checked, onToggle, disabled }) {
  if (!entregas.length) return null;
  const total = somaTroco(entregas);
  const um = entregas.length === 1 ? entregas[0] : null;
  return (
    <div className={`rounded-xl border-2 p-3 ${checked ? "border-emerald-400 bg-emerald-50" : "border-amber-400 bg-amber-50"}`} data-testid="troco-saida-card" data-ok={checked}>
      <div className="flex items-center gap-2 text-[11px] font-extrabold uppercase text-amber-900"><HandCoins className="w-4 h-4" />Troco para levar</div>
      {um ? (
        <div className="mt-1 text-sm">
          <span className="mono text-2xl font-extrabold text-amber-900" data-testid="troco-saida-total">{fmtBRL(um.troco)}</span>
          <div className="text-xs text-slate-600">cliente paga {fmtBRL(um.troco_para || (Number(um.valor) || 0) + (Number(um.troco) || 0))} · pedido {fmtBRL(um.valor || 0)}</div>
        </div>
      ) : (
        <div className="mt-1">
          <TrocoLista entregas={entregas} />
          <div className="flex justify-between items-center pt-1 border-t border-amber-300 text-sm font-bold">Total <span className="mono text-xl text-amber-900" data-testid="troco-saida-total">{fmtBRL(total)}</span></div>
        </div>
      )}
      <Button type="button" onClick={onToggle} disabled={disabled} variant={checked ? "default" : "outline"}
        className={`w-full mt-2 h-11 font-bold ${checked ? "bg-emerald-600 hover:bg-emerald-700" : "border-amber-500 text-amber-900 hover:bg-amber-100"}`} data-testid="troco-saida-check">
        {checked ? <><CheckCircle2 className="w-5 h-5 mr-2" />Troco recebido do balcão</> : <><HandCoins className="w-5 h-5 mr-2" />{um ? `Recebi o troco de ${fmtBRL(total)} do balcão` : `Recebi ${fmtBRL(total)} em troco (${entregas.length} pedidos)`}</>}
      </Button>
    </div>
  );
}

/** Banner laranja (rede de segurança): entregas em rota com troco ainda não conferido. */
export function TrocoSaidaBanner({ pendentes, onOpen }) {
  if (!pendentes.length) return null;
  return (
    <button type="button" onClick={onOpen} className="w-full text-left rounded-2xl border-2 border-amber-500 bg-amber-50 p-3 shadow-md flex items-center gap-3 active:scale-[0.99]" data-testid="troco-saida-banner">
      <HandCoins className="w-7 h-7 text-amber-700 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-extrabold text-amber-900 uppercase tracking-wide">Troco não conferido: {pendentes.length} pedido(s) · <span className="mono">{fmtBRL(somaTroco(pendentes))}</span></div>
        <div className="text-xs text-amber-800">Toque para confirmar que recebeu o troco do balcão.</div>
      </div>
    </button>
  );
}

/** Dialog de confirmação rápida (após scanner/busca ou pelo banner): 1 toque confirma tudo. */
export function TrocoSaidaDialog({ open, entregas, onClose, onConfirm, saving }) {
  const total = somaTroco(entregas);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-sm" data-testid="troco-saida-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-900"><HandCoins className="w-6 h-6" />Confirme o troco recebido</DialogTitle>
          <DialogDescription>Confira com o balcão o troco destes {entregas.length} pedido(s) antes de sair.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3">
          <TrocoLista entregas={entregas} />
          <div className="flex justify-between items-center pt-1 border-t border-amber-300 text-sm font-bold">Total <span className="mono text-xl text-amber-900" data-testid="troco-saida-dialog-total">{fmtBRL(total)}</span></div>
        </div>
        <div className="flex gap-2 mt-2">
          <Button variant="outline" className="flex-1 h-11" onClick={onClose} disabled={saving} data-testid="troco-saida-depois">Depois</Button>
          <Button className="flex-[1.6] h-11 bg-emerald-600 hover:bg-emerald-700 font-bold" onClick={onConfirm} disabled={saving || !entregas.length} data-testid="troco-saida-confirmar">
            {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}Recebi {fmtBRL(total)} em troco
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
